import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ZoomIn, ZoomOut, Maximize2, X, Maximize } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatPercentage } from '@/utils/formatCurrency';
import { formatCombinedGreeting, formatSmartGreeting } from '@/utils/pre-approval/greetingHelpers';
import { formatLoanType, formatPropertyType, formatOccupancy } from '@/utils/pre-approval/formatters';

// Template types (matching VisualPreApprovalWizard)
interface TemplateLayout {
  id: string;
  name: string;
  pageSize: 'letter' | 'a4';
  orientation: 'portrait' | 'landscape';
  margins: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  elements: Array<{
    id: string;
    type: 'text' | 'image' | 'divider' | 'signature';
    content?: string;
    imageUrl?: string;
    x: number;
    y: number;
    width: number;
    height: number;
    styles: {
      fontSize: number;
      fontFamily: string;
      fontWeight: 'normal' | 'bold';
      fontStyle: 'normal' | 'italic';
      textDecoration: 'none' | 'underline';
      textAlign: 'left' | 'center' | 'right';
      color: string;
      backgroundColor?: string;
      padding: number;
      borderRadius: number;
      border?: string;
      lineHeight?: number;
    };
  }>;
  createdAt: string;
  updatedAt: string;
}

interface VisualTemplatePreviewProps {
  template: TemplateLayout;
  formData: {
    borrowerFirstName?: string;
    borrowerLastName?: string;
    borrowerEmail?: string;
    borrowerPhone?: string;
    propertyAddress?: string;
    propertyCity?: string;
    propertyState?: string;
    propertyZip?: string;
    loanAmount?: string;
    purchasePrice?: string;
    downPayment?: string;
    downPaymentPercent?: string;
    expirationDate?: string;
    specialConditions?: string;
    [key: string]: any;
  };
  loanOfficerSignatureDataUrl?: string;
  hasLoanOfficerSignature?: boolean;
  loanOfficerSignatureDate?: string;
  loanOfficerSignatureIpAddress?: string;
  className?: string;
  hideControls?: boolean; // Hide header and zoom controls when parent provides them
  externalScale?: number; // Scale controlled by parent (used when hideControls is true)
}

// Dev aid: flip on to render dashed guides at the template margins.
const SHOW_MARGIN_GUIDES = false;

export const VisualTemplatePreview: React.FC<VisualTemplatePreviewProps> = React.memo(({
  template,
  formData,
  loanOfficerSignatureDataUrl,
  hasLoanOfficerSignature,
  loanOfficerSignatureDate,
  loanOfficerSignatureIpAddress,
  className,
  hideControls = false,
  externalScale
}) => {
  const [internalScale, setInternalScale] = useState(0.6);
  // Use external scale when hideControls is true and externalScale is provided
  const scale = (hideControls && externalScale !== undefined) ? externalScale : internalScale;
  const setScale = setInternalScale;
  const [isFullScreen, setIsFullScreen] = useState(false);
  const previewContainerRef = React.useRef<HTMLDivElement>(null);

  // Debounce form data to avoid excessive re-renders
  const debouncedFormData = useDebounce(formData, 300);
  
  // Calculate scale to fit the page in the preview container
  const fitToPage = useCallback(() => {
    if (!previewContainerRef.current || !template) {
      return;
    }
    
    const container = previewContainerRef.current;
    const containerWidth = container.clientWidth - 32; // Subtract padding
    const containerHeight = container.clientHeight - 100; // Subtract controls height and padding
    
    const pageWidth = template.pageSize === 'letter' ? 612 : 595;
    const pageHeight = template.pageSize === 'letter' ? 792 : 842;
    
    const scaleX = containerWidth / pageWidth;
    const scaleY = containerHeight / pageHeight;
    
    // Use the smaller scale to ensure the entire page fits
    const newScale = Math.min(scaleX, scaleY, 1); // Cap at 100%
    setScale(Math.max(0.1, newScale)); // Minimum 10%
  }, [template]);
  
  // Replace template variables with actual form data
  const replaceVariables = useCallback((text: string): string => {
    if (!text) {
      return '';
    }
    
    // Define variable mappings matching the PDF generator
    const isTBD = debouncedFormData.propertyAddress === 'TBD';
    
    const variables: Record<string, string> = {
      // Borrower Information - Using smart greeting helper for consistent formatting
      '{{borrowerGreeting}}': formatCombinedGreeting(
        {
          salutation: debouncedFormData.borrowerSalutation,
          firstName: debouncedFormData.borrowerFirstName,
          lastName: debouncedFormData.borrowerLastName
        },
        {
          salutation: debouncedFormData.coBorrowerSalutation,
          firstName: debouncedFormData.coBorrowerFirstName,
          lastName: debouncedFormData.coBorrowerLastName
        }
      ),
      '{{borrowerNames}}': formatCombinedGreeting(
        {
          salutation: debouncedFormData.borrowerSalutation,
          firstName: debouncedFormData.borrowerFirstName,
          lastName: debouncedFormData.borrowerLastName
        },
        {
          salutation: debouncedFormData.coBorrowerSalutation,
          firstName: debouncedFormData.coBorrowerFirstName,
          lastName: debouncedFormData.coBorrowerLastName
        }
      ),
      '{{borrowerFirstName}}': debouncedFormData.borrowerFirstName || '[First Name]',
      '{{borrowerLastName}}': debouncedFormData.borrowerLastName || '[Last Name]',
      '{{borrowerEmail}}': debouncedFormData.borrowerEmail || '[Email]',
      '{{borrowerPhone}}': debouncedFormData.borrowerPhone || '[Phone]',
      '{{coBorrowerGreeting}}': formatSmartGreeting({
        salutation: debouncedFormData.coBorrowerSalutation,
        firstName: debouncedFormData.coBorrowerFirstName,
        lastName: debouncedFormData.coBorrowerLastName
      }),
      '{{coBorrowerFirstName}}': debouncedFormData.coBorrowerFirstName || '',
      '{{coBorrowerLastName}}': debouncedFormData.coBorrowerLastName || '',
      '{{coBorrowerEmail}}': debouncedFormData.coBorrowerEmail || '',
      '{{coBorrowerPhone}}': debouncedFormData.coBorrowerPhone || '',

      // Loan Information
      '{{loanAmount}}': debouncedFormData.loanAmount ? `$${parseInt(debouncedFormData.loanAmount).toLocaleString()}` : '[Loan Amount]',
      '{{purchasePrice}}': debouncedFormData.purchasePrice ? `$${parseInt(debouncedFormData.purchasePrice).toLocaleString()}` : '[Purchase Price]',
      '{{downPaymentAmount}}': debouncedFormData.downPayment ? `$${parseInt(debouncedFormData.downPayment).toLocaleString()}` : '[Down Payment]',
      '{{downPaymentPercent}}': debouncedFormData.downPaymentPercent ? formatPercentage(debouncedFormData.downPaymentPercent, 1) : '[Down Payment %]',
      '{{loanType}}': debouncedFormData.loanType ? formatLoanType(debouncedFormData.loanType) : '[Loan Type]',
      
      // Property Information
      '{{propertyStreet}}': isTBD ? 'TBD' : (debouncedFormData.propertyAddress || '[Property Address]'),
      '{{propertyCity}}': isTBD ? '' : (debouncedFormData.propertyCity || '[City]'),
      '{{propertyState}}': isTBD ? '' : (debouncedFormData.propertyState || '[State]'),
      '{{propertyZipCode}}': isTBD ? '' : (debouncedFormData.propertyZip || '[ZIP]'),
      '{{propertyType}}': debouncedFormData.propertyType ? formatPropertyType(debouncedFormData.propertyType) : '[Property Type]',
      '{{propertyOccupancy}}': debouncedFormData.propertyOccupancy ? formatOccupancy(debouncedFormData.propertyOccupancy) : '[Occupancy]',
      
      // Dates
      '{{expirationDate}}': debouncedFormData.expirationDate || '[Expiration Date]',
      '{{currentDate}}': new Date().toLocaleDateString(),
      '{{dateOfPreApproval}}': new Date().toLocaleDateString(),
      
      // Loan Officer Information (from formData passed by parent)
      '{{loanOfficerName}}': debouncedFormData.loanOfficerName || '[Loan Officer Name]',
      '{{loanOfficerFirstName}}': debouncedFormData.loanOfficerFirstName || '[First Name]',
      '{{loanOfficerLastName}}': debouncedFormData.loanOfficerLastName || '[Last Name]',
      '{{loanOfficerTitle}}': debouncedFormData.loanOfficerTitle || '[Title]',
      '{{loanOfficerNMLS}}': debouncedFormData.loanOfficerNMLS || '[NMLS#]',
      '{{loanOfficerPhone}}': debouncedFormData.loanOfficerPhone || '[Phone]',
      '{{loanOfficerMobile}}': debouncedFormData.loanOfficerMobile || '[Mobile]',
      '{{loanOfficerEmail}}': debouncedFormData.loanOfficerEmail || '[Email]',
      
      // Company Information (from formData passed by parent)
      '{{companyName}}': debouncedFormData.companyName || '[Company Name]',
      '{{companyNMLS}}': debouncedFormData.companyNMLS || '[Company NMLS#]',
      '{{companyPhone}}': debouncedFormData.companyPhone || '[Company Phone]',
      '{{companyEmail}}': debouncedFormData.companyEmail || '[Company Email]',
      '{{companyWebsite}}': debouncedFormData.companyWebsite || '[Website]',
      '{{companyAddress}}': debouncedFormData.companyAddress || '[Company Address]',
      '{{companyStreet}}': debouncedFormData.companyStreet || '[Street]',
      '{{companyCity}}': debouncedFormData.companyCity || '[City]',
      '{{companyState}}': debouncedFormData.companyState || '[State]',
      '{{companyZip}}': debouncedFormData.companyZip || '[ZIP]',

      // Additional commonly used variables
      '{{loanOfficerWebsite}}': debouncedFormData.companyWebsite || '[Website]',
      '{{loanOfficerSignature}}': hasLoanOfficerSignature && loanOfficerSignatureDataUrl
        ? '__SIGNATURE_IMAGE__' // Special marker for image rendering
        : '_________________________\nLoan Officer E-Signature (Pending)',
      '{{preApprovalDisclaimer}}': debouncedFormData.preApprovalDisclaimer || '',
      '{{equalHousingLender}}': debouncedFormData.equalHousingLender || '',
      
      // Calculated loan values
      '{{ltv}}': debouncedFormData.loanAmount && debouncedFormData.purchasePrice 
        ? `${((parseFloat(debouncedFormData.loanAmount) / parseFloat(debouncedFormData.purchasePrice)) * 100).toFixed(1)}%` 
        : '[LTV]'
    };

    // Mapping between bracket notation (human-readable) and curly notation (camelCase)
    const bracketToCurlyMapping: Record<string, string> = {
      // Borrower fields
      'Borrower Name': '{{borrowerGreeting}}',
      'Borrower Names': '{{borrowerNames}}',
      'Co-Borrower Name': '{{coBorrowerGreeting}}',
      'First Name': '{{borrowerFirstName}}',
      'Last Name': '{{borrowerLastName}}',

      // Loan Officer fields
      'Loan Officer Name': '{{loanOfficerName}}',
      'Loan Officer Title': '{{loanOfficerTitle}}',
      'Loan Officer NMLS': '{{loanOfficerNMLS}}',
      NMLS: '{{loanOfficerNMLS}}',
      Title: '{{loanOfficerTitle}}',
      Phone: '{{loanOfficerPhone}}',
      Mobile: '{{loanOfficerMobile}}',
      'Mobile Phone': '{{loanOfficerMobile}}',
      Email: '{{loanOfficerEmail}}',
      Website: '{{companyWebsite}}',

      // Company fields
      'Company Name': '{{companyName}}',
      'Company NMLS': '{{companyNMLS}}',
      'Company NMLS#': '{{companyNMLS}}',
      'Company Phone': '{{companyPhone}}',
      'Company Email': '{{companyEmail}}',
      'Company Website': '{{companyWebsite}}',
      'Company Address': '{{companyAddress}}',
      'Company Street': '{{companyStreet}}',
      'Company City': '{{companyCity}}',
      'Company State': '{{companyState}}',
      'Company ZIP': '{{companyZip}}',

      // Loan fields
      'Loan Amount': '{{loanAmount}}',
      'Purchase Price': '{{purchasePrice}}',
      'Loan Type': '{{loanType}}',
      'Down Payment': '{{downPaymentAmount}}',
      'Down Payment %': '{{downPaymentPercent}}',
      'Down Payment Percent': '{{downPaymentPercent}}',
      LTV: '{{ltv}}',

      // Property fields
      'Property Address': '{{propertyStreet}}',
      'Property Type': '{{propertyType}}',
      'Property Occupancy': '{{propertyOccupancy}}',
      'Property City': '{{propertyCity}}',
      'Property State': '{{propertyState}}',
      'Property ZIP': '{{propertyZipCode}}',
      ZIP: '{{propertyZipCode}}',

      // Date fields
      'Current Date': '{{currentDate}}',
      'Expiration Date': '{{expirationDate}}'
    };

    // Replace all variables in the text - supporting BOTH {{curly}} and [bracket] notation
    let result = text;

    // First replace {{curly}} notation
    Object.entries(variables).forEach(([variable, value]) => {
      result = result.replace(new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g'), value);
    });

    // Then replace [bracket] notation using mapping
    Object.entries(bracketToCurlyMapping).forEach(([bracketName, curlyKey]) => {
      const value = variables[curlyKey];
      if (value) {
        const bracketRegex = new RegExp(`\\[${bracketName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`, 'g');
        result = result.replace(bracketRegex, value);
      }
    });

    return result;
  }, [debouncedFormData, hasLoanOfficerSignature, loanOfficerSignatureDataUrl]);

  // Render a single template element
  const renderElement = useCallback((element: typeof template.elements[0], index: number) => {
    // Don't scale the positions - they should be relative to the scaled container
    const elementStyles = {
      position: 'absolute' as const,
      left: `${element.x}px`,
      top: `${element.y}px`,
      width: `${element.width}px`,
      height: element.type === 'text' ? 'auto' : `${element.height}px`,
      zIndex: index + 1,
      fontSize: `${element.styles.fontSize}px`,
      fontFamily: element.styles.fontFamily,
      fontWeight: element.styles.fontWeight,
      fontStyle: element.styles.fontStyle,
      textDecoration: element.styles.textDecoration,
      textAlign: element.styles.textAlign as any,
      color: element.styles.color,
      backgroundColor: element.type === 'image' ? 'transparent' : element.styles.backgroundColor,
      padding: `${element.styles.padding}px`,
      borderRadius: `${element.styles.borderRadius}px`,
      border: element.styles.border,
      lineHeight: element.styles.lineHeight
    };

    switch (element.type) {
      case 'text':
        return (
          <div
            key={element.id}
            style={{
              ...elementStyles,
              // Override height for text elements to allow wrapping
              height: 'auto',
              maxHeight: elementStyles.height,
              overflow: 'hidden',
              // Apply padding inside the element bounds
              boxSizing: 'border-box',
              // Handle text wrapping
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              overflowWrap: 'break-word'
            }}
          >
            {(() => {
              const replacedContent = replaceVariables(element.content || '');
              // Check if content contains signature image marker
              if (replacedContent.includes('__SIGNATURE_IMAGE__') && loanOfficerSignatureDataUrl) {
                return (
                  <img
                    src={loanOfficerSignatureDataUrl}
                    alt="Loan Officer Signature"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                );
              }
              return replacedContent;
            })()}
          </div>
        );

      case 'image':
        if (!element.imageUrl && !element.content) {
return null;
}
        return (
          <img
            key={element.id}
            src={element.imageUrl || element.content || ''}
            alt="Template image"
            style={elementStyles}
          />
        );

      case 'divider':
        return (
          <div
            key={element.id}
            style={{
              ...elementStyles,
              borderBottom: element.styles.border || `1px solid ${element.styles.color}`,
              height: '1px'
            }}
          />
        );

      case 'signature':
        // Render actual signature image if available
        if (hasLoanOfficerSignature && loanOfficerSignatureDataUrl) {
          // Format signature date if available
          const formattedDate = loanOfficerSignatureDate
            ? new Date(loanOfficerSignatureDate).toLocaleString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              })
            : null;

          return (
            <div
              key={element.id}
              style={{
                ...elementStyles,
                // Override template placeholder styles for signed state
                border: 'none',
                backgroundColor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px',
                gap: '4px'
              }}
            >
              <img
                src={loanOfficerSignatureDataUrl}
                alt="Loan Officer Signature"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  height: 'auto',
                  display: 'block'
                }}
              />
              {(formattedDate || loanOfficerSignatureIpAddress) && (
                <div style={{
                  fontSize: '10px',
                  color: '#374151',
                  marginTop: '4px',
                  textAlign: 'center'
                }}>
                  {formattedDate && (
                    <div>Signed on: {formattedDate}</div>
                  )}
                  {loanOfficerSignatureIpAddress && (
                    <div>IP: {loanOfficerSignatureIpAddress}</div>
                  )}
                </div>
              )}
            </div>
          );
        }
        // Show placeholder if no signature
        return (
          <div
            key={element.id}
            style={{
              ...elementStyles,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-end',
              padding: '8px',
              gap: '4px'
            }}
          >
            <div style={{
              width: '100%',
              borderBottom: '2px solid #9ca3af',
              marginBottom: '4px'
            }} />
            <span className="text-gray-500 text-xs" style={{ fontSize: '10px' }}>
              Loan Officer E-Signature (Pending)
            </span>
          </div>
        );

      default:
        return null;
    }
  }, [replaceVariables, template]);

  // Memoize the preview content to optimize performance
  // Uses a visual-size wrapper to fix CSS transform layout box issues:
  // transform: scale() changes visual size but NOT layout box, causing centering issues
  const previewContent = useMemo(() => {
    if (!template) {
      return null;
    }

    const pageWidth = template.pageSize === 'letter' ? 612 : 595;
    const pageHeight = template.pageSize === 'letter' ? 792 : 842;
    // Calculate visual dimensions after scale transform
    const visualWidth = pageWidth * scale;
    const visualHeight = pageHeight * scale;

    return (
      // Outer wrapper with visual dimensions - this is what flexbox uses for centering
      <div style={{
        width: `${visualWidth}px`,
        height: `${visualHeight}px`,
        position: 'relative'
      }}>
        {/* Scale transform container - positioned absolutely within visual-size wrapper */}
        <div style={{
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'absolute',
          top: 0,
          left: 0
        }}>
          <div
            className="bg-white shadow-lg relative"
            style={{
              width: `${pageWidth}px`,
              height: `${pageHeight}px`,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Elements are positioned absolutely from the page origin, matching PDF coordinates */}
            {template.elements && Array.isArray(template.elements) && template.elements.map((el, idx) => renderElement(el, idx))}

            {/* Visual margin indicators (optional) */}
            {SHOW_MARGIN_GUIDES && (
              <div
                className="absolute border border-dashed border-gray-300 pointer-events-none"
                style={{
                  top: `${template.margins.top}px`,
                  right: `${template.margins.right}px`,
                  bottom: `${template.margins.bottom}px`,
                  left: `${template.margins.left}px`,
                  width: `${pageWidth - template.margins.left - template.margins.right}px`,
                  height: `${pageHeight - template.margins.top - template.margins.bottom}px`
                }}
              />
            )}
          </div>
        </div>
      </div>
    );
  }, [template, scale, renderElement]);

  // Fit to page on initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      fitToPage();
    }, 100); // Small delay to ensure container is measured correctly
    
    return () => clearTimeout(timer);
  }, [template, fitToPage]);

  // Safety check for template
  if (!template) {
    return null;
  }



  // When hideControls is true, render preview aligned to top for proper scrolling
  if (hideControls) {
    return (
      <div className={cn("w-full min-h-full flex items-start justify-center pt-4", className)}>
        {previewContent}
      </div>
    );
  }

  return (
    <>
      <div className={cn("p-4 h-full flex flex-col", className)}>
        <div className="flex-1 flex flex-col space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Document Preview</h3>
            <span className="text-xs text-muted-foreground">Updates as you type</span>
          </div>

          <div className="flex items-center gap-2 mb-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setScale(Math.max(0.3, scale - 0.1))}
              disabled={scale <= 0.3}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground min-w-[50px] text-center">
              {Math.round(scale * 100)}%
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setScale(Math.min(1, scale + 0.1))}
              disabled={scale >= 1}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fitToPage}
              title="Fit to Page"
            >
              <Maximize className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFullScreen(true)}
            >
              <Maximize2 className="h-4 w-4 mr-1" />
              Full Screen
            </Button>
          </div>

          <div
            ref={previewContainerRef}
            className="flex-1 overflow-auto border rounded-md bg-transparent"
          >
            <div
              className="flex justify-center items-start"
              style={{
                minWidth: 'fit-content',
                minHeight: 'fit-content'
              }}
            >
              <div className="flex justify-center">
                {previewContent}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={isFullScreen} onOpenChange={setIsFullScreen}>
        <DialogContent className="max-w-[90vw] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Full Screen Preview</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto flex justify-center items-start" style={{ maxHeight: 'calc(90vh - 120px)' }}>
            {previewContent}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

VisualTemplatePreview.displayName = 'VisualTemplatePreview';
